<section class="mx-auto w-full max-w-360 p-6 2xl:px-0">
  <!-- Header -->
  <div class="flex flex-col items-end justify-between gap-4 lg:flex-row">
    <div>
      <div
        class="mb-1 inline-flex cursor-pointer items-center gap-1 text-xs font-semibold tracking-widest uppercase transition"
      >
        IQ Analytics
      </div>
      <h1 id="page-title" class="m-0 text-3xl leading-tight font-bold">
        Overview
      </h1>
      <p class="mt-1 text-sm text-gray-400">
        Track network observations and analyze service quality across your
        locations
      </p>
    </div>

    <div class="flex flex-row flex-wrap items-end justify-end gap-2">
      @if (canShowSampleDataToggle()) {
        <div
          class="mt-2 flex h-8 items-end justify-center rounded-full bg-transparent px-1"
          aria-label="Data source toggle"
        >
          <app-toggle
            [ngModel]="showMockData()"
            (ngModelChange)="toggleMockData($event)"
            [label]="showMockData() ? 'Real Data' : 'Sample Data'"
            size="md"
            color="primary"
          >
          </app-toggle>
        </div>
      }
      <app-analytics-time-selector
        [disabled]="isLoading()"
        (selectedOptionChange)="onTimeSelectorChange($event)"
      />
    </div>
  </div>

  <div aria-live="polite" aria-atomic="true">
    <!-- Loading state -->
    @if (isLoading()) {
      <div
        class="my-4 flex animate-pulse flex-col gap-4"
        role="status"
        aria-label="Loading dashboard"
      >
        <!-- Skeleton Row 1 -->
        <div
          class="grid grid-cols-1 overflow-hidden bg-white shadow-sm lg:grid-cols-12"
        >
          <div class="p-4 lg:col-span-9">
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
              @for (i of [0, 1, 2]; track i) {
                <div class="flex flex-col gap-2 p-4">
                  <div class="h-4 w-20 rounded bg-gray-200"></div>
                  <div class="h-16 w-full rounded bg-gray-200"></div>
                </div>
              }
            </div>
          </div>
          <div class="space-y-4 p-4 lg:col-span-3">
            <div class="h-4 w-32 rounded bg-gray-200"></div>
            <div class="mx-auto h-8 w-20 rounded bg-gray-200"></div>
            <div class="h-4 w-28 rounded bg-gray-200"></div>
            <div class="mx-auto h-8 w-24 rounded bg-gray-200"></div>
          </div>
        </div>
        <!-- Skeleton Row 2 -->
        <div
          class="grid grid-cols-1 overflow-hidden bg-white shadow-sm lg:grid-cols-12"
        >
          <div class="flex items-center justify-center p-4 lg:col-span-5">
            <div class="h-48 w-48 rounded-full bg-gray-200"></div>
          </div>
          <div class="space-y-3 p-4 lg:col-span-7">
            <div class="h-4 w-40 rounded bg-gray-200"></div>
            <div class="h-64 w-full rounded bg-gray-200"></div>
          </div>
        </div>
        <span class="sr-only">Loading dashboard…</span>
      </div>
    }

    <!-- Error state & Empty state (HTTP 204) -->
    @if ((hasError() && !isLoading()) || (isNoContent() && !isLoading())) {
      <div
        class="my-4 flex min-h-52 flex-col items-center justify-center bg-white p-12"
      >
        <h2 class="text-center text-lg font-medium">
          No IQ observations in this period
        </h2>
        <p class="text-brand-muted text-center text-sm">
          Try selecting a different time range to see observation data.
        </p>
      </div>
    }
  </div>

  <!-- Dashboard content -->
  @if (dashboard() && !isLoading()) {
    <div class="my-4 flex flex-col gap-4">
      <!-- Row 1: Observe | Monitor | Investigate | Aggregated IQ ticket stats -->
      <div
        class="grid grid-cols-1 gap-4 divide-y divide-gray-200 overflow-hidden lg:grid-cols-12 lg:divide-x lg:divide-y-0"
      >
        <div class="bg-white shadow-sm lg:col-span-9">
          <app-iq-observation-tiles
            class="block h-full"
            [observationCounts]="dashboard()!.observationCounts"
            [rateOfChange]="dashboard()!.rateOfChange"
          />
        </div>

        <div class="bg-white shadow-sm lg:col-span-3">
          <app-iq-ticket-tiles
            [metrics]="ticketMetrics()"
            [isLoading]="isLoadingTicketMetrics()"
          />
        </div>
      </div>

      <!-- Row 2: Pie Chart | Observations Over Time -->
      <div
        class="grid grid-cols-1 divide-y divide-gray-200 overflow-hidden bg-white shadow-sm lg:grid-cols-12 lg:divide-x lg:divide-y-0"
      >
        <div class="min-h-0 lg:col-span-5">
          <app-iq-aggregated-use-case-pie-chart
            [data]="dashboard()!.useCaseSummary"
            class="block h-full w-full"
          />
        </div>

        <div class="lg:col-span-7">
          <app-analytics-widget [isNoContent]="false" [loading]="false">
            <app-iq-observations-chart
              [data]="dashboard()!.timeSeries"
              [begin]="currentRange()!.begin"
              [end]="currentRange()!.end"
            />
          </app-analytics-widget>
        </div>
      </div>
    </div>
  }
</section>
